/**
 * The report is pasted to a developer: no query strings or hashes (they can carry tokens), no
 * client file paths, no inline data. Pure string functions, run under node:test.
 */

/** `https://host/a/b?x=1#y` -> `https://host/a/b`. Anything that does not parse as a URL goes through redactText. */
export function redactUrl(url: string): string {
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

const URL_RE = /\b(?:https?|blob|wss?):[^\s"'<>)\]]+/gi;
const DATA_URL_RE = /\bdata:[^\s"'<>)]{24,}/gi;
const FILE_URL_RE = /\bfile:\/\/[^\s"'<>)]*/gi;
const WINDOWS_PATH_RE = /\b[A-Za-z]:\\[^\s"'<>|*?]+/g;
const UNIX_PATH_RE = /(?<![\w./:-])\/(?:Users|home|storage|sdcard|var\/mobile|private\/var)\/[^\s"'<>)]+/g;
const FAKE_PATH_RE = /\bC:\\fakepath\\[^\s"'<>]+/gi;

/** Free text (error messages, console output): URLs lose query/hash, file paths and data URLs are dropped. */
export function redactText(text: string): string {
  return text
    .replace(FILE_URL_RE, "file://<path>")
    .replace(DATA_URL_RE, "data:<omitted>")
    .replace(URL_RE, (url) => redactUrl(url))
    .replace(FAKE_PATH_RE, "<path>")
    .replace(WINDOWS_PATH_RE, "<path>")
    .replace(UNIX_PATH_RE, "<path>");
}

/** Redacts, collapses newlines and cuts to `max` characters (one log line each). */
export function clipLine(text: string, max: number): string {
  const flat = redactText(text).replace(/\s+/g, " ").trim();
  return flat.length > max ? flat.slice(0, max - 1) + "…" : flat;
}

/** `/walk/<id>/scene.json?x=1` -> `/walk/<id>/scene.json` (the service worker's log keeps paths only). */
export function pathOnly(url: string): string {
  try {
    return new URL(url, "http://x").pathname;
  } catch {
    return redactUrl(url);
  }
}
